<template>
    <kiosk-group-details :teamId="props.teamId" :groupId="props.groupId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-sm) justify-center">
            <page-callout section="kiosk-group" class="mb-4" />

            <page-header>Details</page-header>

            <vx-expandable-card class="mb-6">
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col gap-1">
                            <strong>External ID:</strong>
                            <div class="text-sm text-slate-500">
                                {{ group.group_id }}
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Name:</strong>
                            <div class="text-sm text-slate-500">
                                {{ group.name }}
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Time-zone:</strong>
                            <div class="text-sm text-slate-500">
                                {{ group.time_zone ? group.time_zone.split('_').join(' ') : '-' }}
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Team:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="teamLoading"></vx-icon-loading>
                                <div v-else-if="team" class="flex items-center gap-2">
                                    <vx-badge size="xs" color="slate">{{ team.id }}</vx-badge>
                                    <span>{{ team.name }}</span>
                                    <vx-button
                                        class="ml-1"
                                        size="xs"
                                        color="muted"
                                        data-test="go-to-team"
                                        :href="`/kiosk/teams/${team.id}`"
                                    >
                                        Go to Team
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Team Owner:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="teamOwnerLoading"></vx-icon-loading>
                                <div v-else-if="teamOwner" class="flex items-center gap-2">
                                    <div>{{ teamOwner.name }}</div>
                                    <vx-button
                                        color="muted"
                                        size="xs"
                                        data-test="go-to-owner"
                                        :href="`/kiosk/users/${teamOwner.id}`"
                                    >
                                        Go to Owner
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Group Owner:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="groupOwnerLoading"></vx-icon-loading>
                                <div v-else-if="groupOwner" class="flex items-center gap-2">
                                    <div>{{ groupOwner.name }}</div>
                                    <vx-button
                                        color="muted"
                                        size="xs"
                                        data-test="go-to-group-owner"
                                        :href="`/kiosk/users/${groupOwner.id}`"
                                    >
                                        Go to Owner
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Organizational Unit:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="orgUnitLoading"></vx-icon-loading>
                                <div v-else-if="orgUnit" class="flex items-center gap-2">
                                    <vx-badge size="xs" color="slate">{{ orgUnit.id }}</vx-badge>
                                    <span>{{ orgUnit.name }}</span>
                                    <vx-button
                                        class="ml-1"
                                        size="xs"
                                        color="muted"
                                        data-test="go-to-org-unit"
                                        :href="`/kiosk/teams/${group.team_id}/org-units/${orgUnit.id}`"
                                    >
                                        Go to OU
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Users:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="usersCountLoading"></vx-icon-loading>
                                <div v-else-if="usersCount != undefined" class="flex items-center gap-2">
                                    {{ usersCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Pending Invites:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="invitationsCountLoading"></vx-icon-loading>
                                <div v-else-if="invitationsCount != undefined" class="flex items-center gap-2">
                                    {{ invitationsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div
                            v-if="
                                group.address ||
                                group.address_line_2 ||
                                group.city ||
                                group.state ||
                                group.zip ||
                                group.country
                            "
                            class="flex flex-col gap-1"
                        >
                            <strong>Address:</strong>
                            <div class="text-sm text-slate-500">
                                <div v-if="group.address">{{ group.address }}</div>
                                <div v-if="group.address_line_2">{{ group.address_line_2 }}</div>
                                <div v-if="group.city || group.state || group.zip">
                                    {{ [group.city, group.state].filter(Boolean).join(', ') }}
                                    {{ group.zip ? group.zip : '' }}
                                </div>
                                <div v-if="group.country">{{ countryLabel }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card class="mb-6">
                <template v-slot:content>
                    <div class="text-xl font-extrabold mb-4">Custom Attributes</div>
                    <vx-icon-loading spin v-if="customAttributesLoading"></vx-icon-loading>

                    <div v-else-if="!customAttributes || !customAttributes.length" class="text-slate-500">
                        No custom attributes yet.
                    </div>

                    <div v-else class="flex flex-col gap-4">
                        <div
                            v-for="customAttribute in customAttributes"
                            :key="customAttribute.key"
                            class="flex flex-col gap-1"
                        >
                            <strong>{{ customAttribute.key }}:</strong>
                            <div class="text-sm text-slate-500 break-all">
                                {{ customAttribute.value }}
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
        </div>
    </kiosk-group-details>
</template>

<script setup>
import KioskGroupDetails from '~/components/kiosk/kiosk-groups/details/KioskGroupDetails.vue';
import { VxBadge, VxButton, VxExpandableCard, VxIconLoading } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { computed } from 'vue';
import { countries } from '~/constants/countries';

const {
    group,
    onMountedGroup,

    team,
    teamLoading,

    teamOwner,
    teamOwnerLoading,

    groupOwner,
    groupOwnerLoading,
    getGroupOwner,

    usersCount,
    usersCountLoading,
    getUsersCount,

    invitationsCount,
    invitationsCountLoading,
    getInvitationsCount,

    orgUnit,
    orgUnitLoading,

    customAttributes,
    customAttributesLoading,
    getCustomAttributes,
} = useGroupDetails();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
});

const countryLabel = computed(
    () => countries.find((country) => country.value === group.value.country)?.label || group.value.country
);

onMountedGroup(() => {
    getGroupOwner();
    getUsersCount();
    getInvitationsCount();
    getCustomAttributes();
});
</script>
